<div *ngIf="loading; else loaded">
  <h3 class="panel-title"><i class="fa-solid fa-spinner fa-spin"></i> Loading...</h3>
</div>
<ng-template #loaded>
  <div class="flex-left-column light-control-host">
    <div class="flex-center-row">
      <h3 class="panel-title">Launch test of "{{ !!application ? application.name : '' }}"</h3>
    </div>
    <div class="margin-top">
      Select applications to test recovery to {{ !!application ? application.targetRegion : '' }}
    </div>
    <!-- Select All Checkbox -->
    <div class="select-all-container resource-type flex-center-row margin-top" *ngIf="applications.length > 0">
      <input
        type="checkbox"
        class="arpio-checkbox"
        [checked]="areAllApplicationsSelected()"
        (change)="toggleSelectAllApplications($event.target.checked)"
        [disabled]="applications.length === 0"
      />
      <div class="select-all-label" (click)="toggleSelectAllApplications(!areAllApplicationsSelected())">
        {{ areAllApplicationsSelected() ? 'Deselect All' : 'Select All' }}
      </div>
    </div>
    <div class="flex-center-row full-width margin-top">
      <div id="applicationSelectionList" class="light-content-panel spacer">
        <div class="flex-center-row" *ngFor="let application of applications; trackBy: applicationTrackBy">
          <ng-container *ngLet="computeApplicationRecoveryPoint(application.applicationObj) as appRp">
            <input
              class="arpio-checkbox"
              type="checkbox"
              (input)="toggleApplicationSelection(application.applicationObj.applicationId)"
              [checked]="applicationIsChecked(application.applicationObj)"
            />
            <div class="application-name margin-left">{{ application.applicationObj.name }}</div>
            <div class="spacer"></div>
            <ng-container *ngIf="application.selected">
              <div class="flex-center-row" *ngIf="appRp; else noMatchingRpBlock">
                <ng-container *ngIf="showLock(appRp)">
                  <i class="fa-utility fa-semibold fa-lock"></i>
                </ng-container>
                <div class="recovery-timestamp-text half-margin-left">
                  {{ recoversToTimeString(application.applicationObj, appRp) }}
                </div>
              </div>
              <ng-template #noMatchingRpBlock
                ><div class="recovery-timestamp-error">No matching recovery points</div></ng-template
              >
            </ng-container>
          </ng-container>
        </div>
      </div>
    </div>

    <ng-container *ngIf="showRetainedRpsCheckbox">
      <div class="flex-center-row margin-top">
        <input
          id="only-protected-rps"
          class="arpio-checkbox"
          type="checkbox"
          (input)="computeApplicationsRecoveryPoint()"
          [(ngModel)]="onlyProtectedRps"
        />
        <label for="only-protected-rps">
          <div class="flex-center-row">
            <h4 class="half-margin-left">Restrict to indefinitely-retained Recovery Points</h4>
            <i class="fa-utility fa-semibold fa-lock half-margin-left"></i>
          </div>
        </label>
      </div>
    </ng-container>

    <ng-container *ngIf="canAdjustRecoveryPoint; else cantAdjustRecoveryPoint">
      <div class="double-margin-top flex-center-row full-width">
        <h4 class="spacer">Recovery Point</h4>
        <h4 class="">{{ recoveryPointTime }}</h4>
      </div>
      <rp-range-slider
        class="full-width"
        [recoveryPoints]="recoveryPoints"
        [enableRealTimeRecovery]="enableRealTimeRecovery"
        (update)="recoveryPointChanged($event)"
      >
      </rp-range-slider>
    </ng-container>
    <recovery-time-info [realTime]="selectedIsRealTime" [snapshotTime]="recoveryPointSnapshotTime"></recovery-time-info>

    <ng-container *ngIf="accountService.networkSandboxEnabled">
      <form [formGroup]="launchTestSettingsFormModel">
        <div class="flex-center-row">
          <input
            id="sandbox-checked"
            class="arpio-checkbox slight-margin-top"
            type="checkbox"
            formControlName="sandboxChecked"
          />
          <label for="sandbox-checked">
            <h4 class="half-margin-left">Enable Network Sandbox</h4>
          </label>
        </div>
        <div *ngIf="isAzureApplication">
          Block outbound internet connectivity from Virtual Network resources to prevent possible interaction with
          production services.
          <a href="{{ uiService.azureNetworkSandboxDocsUrl }}" target="_blank">Learn more.</a>
        </div>
        <div *ngIf="!isAzureApplication">
          Block outbound internet connectivity from VPC resources to prevent possible interaction with production
          services.
          <a href="{{ uiService.networkSandboxDocsUrl }}" target="_blank">Learn more.</a>
        </div>

        <!-- Radio Button Section -->
        <ng-container *ngIf="accountService.networkSandboxWhitelistEnabled && networkSandboxChecked">
          <div class="sandbox-options">
            <!-- Advanced Sandbox Option -->
            <ng-container *ngIf="showAdvancedSandboxOptions && !isAzureApplication">
              <div>
                <input
                  type="radio"
                  id="advanced-sandbox"
                  class="arpio-radio"
                  [checked]="advancedNetworkSandbox"
                  (change)="setAdvancedNetworkSandbox(true)"
                />
                <label for="advanced-sandbox">Manage outbound access by Network Firewall service</label>
              </div>
            </ng-container>

            <!-- Advanced Sandbox Content -->
            <div *ngIf="advancedNetworkSandbox" class="nested-content">
              <h5 class="margin-top">Allow outbound access to these domains</h5>
              <p-scrollPanel [style]="{ width: '100%', maxHeight: '150px' }">
                <string-list-control
                  class="spacer"
                  [validationFunction]="isValidDomain"
                  [placeholderText]="domainAllowlistPlaceholderText"
                  [frozen]="sandboxFrozen"
                  (validated)="sandboxDomainsValidated($event)"
                  formControlName="sandboxAllowedDomains"
                ></string-list-control>
              </p-scrollPanel>

              <div *ngIf="!sandboxFrozen && suggestedDomains.length > 0">
                <h5>Suggested Domains</h5>
                <p-scrollPanel [style]="{ width: '100%', maxHeight: '150px' }">
                  <div class="suggestion" *ngFor="let suggestion of suggestedDomains">
                    <button class="arpio-button" (click)="addAllowedDomain(suggestion.value)">
                      <i class="fa-solid fa-plus"></i>
                    </button>
                    {{ suggestion.value }} <span class="suggestion-reason">({{ suggestion.reason }})</span>
                  </div>
                </p-scrollPanel>
              </div>

              <!-- Reuse CIDR Block Section -->
              <ng-container *ngTemplateOutlet="cidrBlocksTemplate"></ng-container>
            </div>

            <!-- Basic Sandbox Option -->
            <ng-container *ngIf="showAdvancedSandboxOptions && !isAzureApplication">
              <div>
                <input
                  type="radio"
                  id="basic-sandbox"
                  class="arpio-radio"
                  [checked]="!advancedNetworkSandbox"
                  (change)="setAdvancedNetworkSandbox(false)"
                />
                <label for="basic-sandbox">Manage outbound access by Security Group rules</label>
              </div>
            </ng-container>

            <!-- Basic Sandbox Content -->
            <div *ngIf="!advancedNetworkSandbox && !isAzureApplication" class="nested-content">
              <!-- Reused CIDR Block Section -->
              <ng-container *ngTemplateOutlet="cidrBlocksTemplate"></ng-container>

              <div class="nested-checkbox">
                <label for="sandbox-allow-s3">
                  <input
                    id="sandbox-allow-s3"
                    class="arpio-checkbox"
                    type="checkbox"
                    formControlName="allowS3Checked"
                  />
                  Allow outbound access to S3 IPs
                </label>
              </div>
              <div class="nested-checkbox half-margin-top">
                <label>
                  <input
                    id="sandbox-allow-dynamo"
                    class="arpio-checkbox"
                    type="checkbox"
                    formControlName="allowDynamoChecked"
                  />
                  Allow outbound access to DynamoDB IPs
                </label>
              </div>
            </div>
          </div>
        </ng-container>

        <!-- Reusable Template for CIDR Block Section -->
        <ng-template #cidrBlocksTemplate>
          <h5>Allow outbound access to these CIDR blocks</h5>
          <p-scrollPanel [style]="{ width: '100%', maxHeight: '150px' }">
            <string-list-control
              class="spacer"
              [validationFunction]="isValidCidr"
              [normalizeFunction]="normalizeCidr"
              [placeholderText]="whitelistPlaceholderText"
              [frozen]="sandboxFrozen"
              (validated)="whitelistValidated($event)"
              formControlName="sandboxWhitelist"
            ></string-list-control>
          </p-scrollPanel>
        </ng-template>

        <details *ngIf="showAdvancedOptions" class="margin-top">
          <summary class="advanced-summary">Advanced Options</summary>
          <div class="flex-center-row nested-content">
            <div class="nested-checkbox">
              <label>
                <input class="arpio-checkbox slight-margin-top" type="checkbox" formControlName="forceNewTargetPlans" />
                Replan placement of recovery subnets
              </label>
            </div>
          </div>
        </details>
      </form>

      <div *ngIf="this.sandboxFrozen" id="frozen-warning" class="notice-block">
        Network sandbox settings cannot be changed because another {{ uiService.productName }}
        App is already in RECOVERY TEST.
      </div>
    </ng-container>
    <ng-template #cantAdjustRecoveryPoint>
      <div class="double-margin-top flex-center-row full-width">
        <h4 class="spacer">Recovery Point Locked At {{ recoveryPointTime }}</h4>
      </div>
    </ng-template>
    <ng-container *ngIf="currentlySyncingMessage">
      <div class="error margin-top flex-center-row full-width">
        <p class="full-width">
          Arpio is applying your latest recovery point. Once it's done applying, you will be able to test your recovery.
        </p>
      </div>
      <div class="error flex-center-row full-width">
        <div class="spacer"></div>
        <h1><i class="fa-solid fa-spinner fa-spin"></i></h1>
        <div class="spacer"></div>
      </div>
    </ng-container>
    <div class="flex-center-row margin-top full-width">
      <div class="spacer"></div>
      <button class="arpio-secondary-button" (click)="onCancelClick()">CANCEL</button>
      <button
        busy-button
        [busy]="launching"
        id="test-button"
        class="arpio-button margin-left"
        (click)="onLaunchClick()"
        [disabled]="!canLaunchTest"
      >
        TEST RECOVERY
      </button>
      <div class="spacer"></div>
    </div>
  </div>
</ng-template>
